<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { api } from '../../../../api/Api'
import Toast from '../../../../services/Toast'

import TextInput from '../../../../components/ui/form/TextInput.vue'
import TextAreaInput from '../../../../components/ui/form/TextAreaInput.vue'
import NumberInput from '../../../../components/ui/form/NumberInput.vue'
import CheckInput from '../../../../components/ui/form/CheckInput.vue'
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue'
import SearchSelectMultiItems from '../../../../components/ui/form/SearchSelectMultiItems.vue'
import FileUpload from '../../../../components/ui/form/FileUpload.vue'
import RichTextEditor from '../../../../components/ui/form/RichTextEditor.vue'
import IconComponent from '../../../../components/ui/common/IconComponent.vue'

/**
 * Singleton page for the storefront's "Ecommerce Setup". Loads the lone row
 * via `singleton` (creating one server-side if absent), then writes
 * everything — including all six child collections — back through `update`.
 *
 * Not a modal: the previous list+modal pattern doesn't fit a one-row-per-
 * tenant setup, and the form has enough surface that a side-scrolling modal
 * stops being usable. Layout follows the CompanyInformationListView
 * precedent, which is also "ListView" by file name but is actually a page.
 */

const endPoint = '/clients/system/company/ecommerce-setups/'

const loading = ref(true)
const saving = ref(false)
const data = ref<any>(null)

/**
 * Ecommerce setup is a singleton settings page — always editable, no
 * "view" mode the way modal-style forms in the codebase have. Kept as a
 * ref anyway because `FileUpload` and the other form atoms across the
 * codebase expect `:readonly` to be present in their standard call site,
 * and matching that signature keeps the props order consistent with
 * CompanyInformationListView.
 */
const isReadOnly = ref(false)

const currencies = [
  { id: 'MVR', name: 'MVR' },
  { id: 'USD', name: 'USD' },
]

// Branding and Quotation operations were rolled into Identity and Order
// notifications respectively (per the spec) — neither has its own pane
// anymore. Brand assets (logo / favicon / hero image / brand color) plus
// commerce essentials (warehouse / currency / price list / tax-inclusive /
// items selection / allowed payment types) all surface from the Identity
// pane below. Quotation-specific notification fields merged into the
// unified Order notifications pane.
// "Commerce" pane removed per spec — its surviving knobs (guest checkout
// / show prices / minimum order / default customer group) were dropped
// and the rest (currency, price list, tax-inclusive, only-selected-items,
// allowed payment types) were already living on Identity.
const sections = [
  { id: 'identity', label: 'Identity' },
  { id: 'announcement', label: 'Announcement' },
  { id: 'hero', label: 'Hero' },
  { id: 'value_props', label: 'Value props' },
  { id: 'promo_cards', label: 'Promo cards' },
  { id: 'nav', label: 'Top navigation' },
  { id: 'footer', label: 'Footer' },
  { id: 'pages', label: 'Pages' },
  { id: 'faqs', label: 'FAQs' },
  { id: 'contact', label: 'Contact & social' },
  { id: 'catalog', label: 'Catalog display' },
  { id: 'seo', label: 'SEO' },
  { id: 'notifications', label: 'Order notifications' },
  { id: 'policies', label: 'Policies' },
  { id: 'featured', label: 'Featured content' },
  { id: 'operational', label: 'Operational' },
]

/**
 * Active section. Only the picked one renders so the page stays legible
 * instead of stacking 17 long sections at once. Default to the first.
 */
const activeSection = ref<string>('identity')

function pickSection(id: string) {
  activeSection.value = id
  // Reset the scroll so the picked section starts at the top of the viewport
  // even if the previous one was scrolled mid-way.
  requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
}

function onSectionChange(e: Event) {
  pickSection((e.target as HTMLSelectElement).value)
}

const activeLabel = computed(
  () => sections.find((s) => s.id === activeSection.value)?.label ?? '',
)

onMounted(async () => {
  loading.value = true
  try {
    const r = await api.post(endPoint + 'singleton', {})
    if (r.success) {
      data.value = withChildDefaults(r.data || {})
    }
  } finally {
    loading.value = false
  }
  // Pre-load POS stations so the Notifications pane's filtered list
  // is ready as soon as the admin picks an outlet.
  loadStations()
  // Featured-content dropdowns (categories / brands / products) read
  // from the same backend list endpoints other forms use.
  loadFeaturedSources()
})

/* ─── Featured-content option lists ───
 * Loaded once at page mount and handed to the three SearchSelectMultiItems
 * pickers via :options + :remote so the admin picks from real searchable
 * dropdowns instead of free-typing CSV ids. */
const allFeaturedCategories = ref<{ id: string; name: string }[]>([])
const allFeaturedBrands = ref<{ id: string; name: string }[]>([])
const allFeaturedProducts = ref<{ id: string; name: string }[]>([])
// Identity-pane "Only selected item groups" multi-pick is fed the same
// way the featured-content pickers are: pre-fetched once on mount and
// handed to SearchSelectMultiItems via :options + :remote=true. That
// bypasses the component's localStorage cache (keyed off `label`,
// which can serve stale empty arrays from a previous form session
// where the same label collided) and keeps the dropdown explicit.
const allItemGroups = ref<{ id: string; name: string }[]>([])

async function loadFeaturedSources() {
  try {
    const [cats, brands, items, groups] = await Promise.all([
      api.post('inventory/item-management/item-categories/list', {}, { skipLoading: true } as any),
      api.post('inventory/item-management/brands/list', {}, { skipLoading: true } as any),
      api.post('inventory/item-management/items/get-list', {}, { skipLoading: true } as any),
      api.post('inventory/item-management/item-groups/list', {}, { skipLoading: true } as any),
    ])
    if (cats?.success && Array.isArray(cats.data)) {
      allFeaturedCategories.value = cats.data.map((r: any) => ({ id: String(r.id), name: r.name }))
    }
    if (brands?.success && Array.isArray(brands.data)) {
      allFeaturedBrands.value = brands.data.map((r: any) => ({ id: String(r.id), name: r.name }))
    }
    if (items?.success && Array.isArray(items.data)) {
      allFeaturedProducts.value = items.data.map((r: any) => ({
        id: String(r.id),
        name: r.code ? `${r.code} — ${r.name ?? ''}` : (r.name ?? `Item #${r.id}`),
      }))
    }
    if (groups?.success && Array.isArray(groups.data)) {
      allItemGroups.value = groups.data.map((r: any) => ({ id: String(r.id), name: r.name }))
    }
  } catch (e) {
    console.warn('Failed to preload featured-content sources', e)
  }
}

/* ─── POS stations — outlet-filtered ───
 * The Notifications pane's POS-station multi-pick should populate ONLY
 * from outlets the admin has currently selected. We fetch the full
 * `(id, name, outlets_id)` list once at page load via the canonical
 * `system/company/outlets/pos-stations` action (extended to optionally
 * return outlets_id), then derive the visible list reactively. */
const allStations = ref<{ id: string; name: string; outlets_id: string | number }[]>([])

async function loadStations() {
  try {
    const r = await api.post('system/company/outlets/pos-stations', {})
    if (r?.success && Array.isArray(r.data)) {
      allStations.value = r.data.map((s: any) => ({
        id: String(s.id),
        name: s.name,
        outlets_id: s.outlets_id,
      }))
    }
  } catch (e) {
    console.warn('Failed to preload POS stations', e)
  }
}

const filteredStations = computed(() => {
  const picked = new Set(
    (data.value?.notification_outlets_ids ?? []).map((id: any) => String(id)),
  )
  // Spec: stations are loaded only when at least one outlet is picked.
  if (picked.size === 0) return []
  return allStations.value.filter((s) => picked.has(String(s.outlets_id)))
})

/* When outlets change, drop any previously-saved station ids whose
 * outlet is no longer in the picked set, so the persisted data matches
 * the visible pills exactly. Without this, a hidden station id can
 * silently linger on the row across saves. */
watch(
  () => data.value?.notification_outlets_ids,
  () => {
    if (!data.value) return
    // Bail until the stations endpoint has populated allStations —
    // the singleton-load watcher would otherwise fire BEFORE the
    // async station list arrives, finding filteredStations empty
    // and silently stripping every saved POS station id off the
    // row even though the outlets they belong to are still picked.
    if (allStations.value.length === 0) return
    const validIds = new Set(filteredStations.value.map((s) => String(s.id)))
    const current = (data.value.notification_outlet_pos_stations_ids ?? []) as any[]
    data.value.notification_outlet_pos_stations_ids = current
      .map(String)
      .filter((id) => validIds.has(id))
  },
  { deep: true },
)

/**
 * Make sure every child collection is a real array so v-for / v-model are
 * happy even on a fresh singleton row that hasn't been saved yet. Also
 * splits the CSV-stored multi-pick columns into real arrays for the
 * `SearchSelectMultiItems` components (re-joined into CSV at save time).
 */
function withChildDefaults(d: any): any {
  d.hero_slides = d.hero_slides || []
  d.pages = d.pages || []
  d.value_props = d.value_props || []
  d.promo_cards = d.promo_cards || []
  d.nav_items = d.nav_items || []
  d.footer_columns = (d.footer_columns || []).map((c: any) => ({
    ...c,
    links: c.links || [],
  }))
  d.faqs = d.faqs || []
  // CSV fields → arrays for the multi-pick components. We migrate any
  // legacy singular `notification_outlet_pos_stations_id` into the new
  // CSV-backed `_ids` field so the admin's prior single station shows up
  // pre-selected after the first load.
  d.selected_items_ids = csvToArray(d.selected_items_ids)
  // Item-group scoping mirrors the items pattern — CSV on disk, array
  // on the form so SearchSelectMultiItems sees a real list. CSV-rejoin
  // at save() time.
  d.only_selected_item_groups = d.only_selected_item_groups ?? false
  d.selected_item_groups_ids = csvToArray(d.selected_item_groups_ids)
  d.allowed_payment_types_ids = csvToArray(d.allowed_payment_types_ids)
  d.notification_user_ids = csvToArray(d.notification_user_ids)
  d.notification_outlets_ids = csvToArray(d.notification_outlets_ids)
  d.notification_outlet_pos_stations_ids = csvToArray(
    d.notification_outlet_pos_stations_ids
      || (d.notification_outlet_pos_stations_id
        ? String(d.notification_outlet_pos_stations_id)
        : ''),
  )
  // Featured-content pickers are CSV on disk too — split for the
  // SearchSelectMultiItems bindings, re-joined to CSV at save time.
  d.featured_categories_ids = csvToArray(d.featured_categories_ids)
  d.featured_brands_ids = csvToArray(d.featured_brands_ids)
  d.featured_items_ids = csvToArray(d.featured_items_ids)
  return d
}

/* ─── CSV ↔ array helpers ──────────────────────────────
 * Multi-pick columns are persisted as comma-separated strings (SQL Server
 * parity, mirrors `notification_user_ids`). The pick components bind to
 * arrays, so we split on load and re-join just before save. */
function csvToArray(value: any): string[] {
  if (Array.isArray(value)) return value.map(String)
  if (!value) return []
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '')
}
function arrayToCsv(value: any): string {
  return Array.isArray(value) ? value.join(',') : (value ?? '')
}

/* ─── Compulsory-field guard ───
 * `missing` is a reactive flag-map that the Identity template reads to
 * paint a red ring around any field the spec marks as compulsory but
 * which the admin hasn't filled in. Computed once at save time so the
 * fields don't light up red as soon as the page loads — only after the
 * admin attempts to save with an empty value. */
const COMPULSORY_FIELDS: { key: string; label: string; isArray?: boolean }[] = [
  { key: 'warehouses_id', label: 'Warehouse' },
  { key: 'sales_channels_id', label: 'Sales channel' },
  { key: 'document_users_id', label: 'User for storefront documents' },
  { key: 'site_name', label: 'Site name' },
  { key: 'tagline', label: 'Tag line' },
  { key: 'copyright_text', label: 'Copyright' },
  { key: 'site_url', label: 'Site URL' },
  { key: 'default_currency', label: 'Currency' },
  { key: 'default_price_lists_id', label: 'Price list' },
  { key: 'allowed_payment_types_ids', label: 'Allowed payment types', isArray: true },
]

const missing = ref<Record<string, boolean>>({})

function checkCompulsory(): string[] {
  const next: Record<string, boolean> = {}
  const missingLabels: string[] = []
  for (const f of COMPULSORY_FIELDS) {
    const v = data.value?.[f.key]
    const empty = f.isArray
      ? (!Array.isArray(v) || v.length === 0)
      : (v === null || v === undefined || v === '')
    next[f.key] = empty
    if (empty) missingLabels.push(f.label)
  }
  missing.value = next
  return missingLabels
}

async function save() {
  const missingLabels = checkCompulsory()
  if (missingLabels.length > 0) {
    Toast.show(
      'Required: ' + missingLabels.join(', '),
      'warning',
    )
    return
  }
  saving.value = true
  try {
    // Marshal the array-bound multi-pick fields back into the CSV strings
    // the columns expect. Spread first so we don't mutate the live ref.
    const payload = {
      ...data.value,
      selected_items_ids: arrayToCsv(data.value.selected_items_ids),
      selected_item_groups_ids: arrayToCsv(data.value.selected_item_groups_ids),
      allowed_payment_types_ids: arrayToCsv(data.value.allowed_payment_types_ids),
      notification_user_ids: arrayToCsv(data.value.notification_user_ids),
      notification_outlets_ids: arrayToCsv(data.value.notification_outlets_ids),
      notification_outlet_pos_stations_ids: arrayToCsv(
        data.value.notification_outlet_pos_stations_ids,
      ),
      featured_categories_ids: arrayToCsv(data.value.featured_categories_ids),
      featured_brands_ids: arrayToCsv(data.value.featured_brands_ids),
      featured_items_ids: arrayToCsv(data.value.featured_items_ids),
    }
    const r = await api.post(endPoint + 'update', payload)
    if (r.success) {
      Toast.show('Storefront saved.')
      data.value = withChildDefaults(r.data || {})
      // Clear the red rings once the save lands successfully.
      missing.value = {}
    }
  } finally {
    saving.value = false
  }
}

/* ─── Items transfer-list (mirror of EcommerceSetupsForm) ──────────────
 * Rendered inside the Identity pane when `only_selected_items` is checked.
 * Loads the catalogue once per session, then lets the admin shuttle rows
 * between Available and Selected. `data.selected_items_ids` is array-bound
 * here and serialised to CSV at save time. */
type ItemRow = { id: string | number; name: string; code?: string }

const allItems = ref<ItemRow[]>([])
const itemsLoading = ref(false)
const itemsLoadedAt = ref<number | null>(null)
const availableFilter = ref('')
const selectedFilter = ref('')
const availableChecked = ref<string[]>([])
const selectedChecked = ref<string[]>([])

const availableItems = computed<ItemRow[]>(() => {
  const picked = new Set((data.value?.selected_items_ids ?? []).map(String))
  const q = availableFilter.value.trim().toLowerCase()
  return allItems.value
    .filter((it) => !picked.has(String(it.id)))
    .filter((it) => !q
      || it.name.toLowerCase().includes(q)
      || (it.code ?? '').toLowerCase().includes(q),
    )
})

const selectedItems = computed<ItemRow[]>(() => {
  const ids = (data.value?.selected_items_ids ?? []).map(String)
  const byId = new Map(allItems.value.map((it) => [String(it.id), it]))
  const q = selectedFilter.value.trim().toLowerCase()
  return ids
    .map((id: string): ItemRow => byId.get(id) ?? { id, name: `Item #${id}` })
    .filter((it: ItemRow) => !q
      || it.name.toLowerCase().includes(q)
      || (it.code ?? '').toLowerCase().includes(q),
    )
})

async function loadCatalogue() {
  if (itemsLoading.value) return
  if (itemsLoadedAt.value && Date.now() - itemsLoadedAt.value < 5 * 60 * 1000) return
  itemsLoading.value = true
  try {
    const resp = await api.post<any[]>('inventory/item-management/items/get-list', {}, { skipLoading: true } as any)
    if (resp?.success && Array.isArray(resp.data)) {
      allItems.value = resp.data.map((r: any) => ({
        id: r.id,
        name: r.name ?? String(r.id),
        code: r.code,
      }))
      itemsLoadedAt.value = Date.now()
    }
  } finally {
    itemsLoading.value = false
  }
}

watch(() => data.value?.only_selected_items, async (now) => {
  if (now) await loadCatalogue()
})

function moveToSelected() {
  if (availableChecked.value.length === 0) return
  const current = new Set((data.value.selected_items_ids ?? []).map(String))
  for (const id of availableChecked.value) current.add(String(id))
  data.value.selected_items_ids = Array.from(current)
  availableChecked.value = []
}
function moveAllToSelected() {
  const current = new Set((data.value.selected_items_ids ?? []).map(String))
  for (const it of availableItems.value) current.add(String(it.id))
  data.value.selected_items_ids = Array.from(current)
  availableChecked.value = []
}
function moveToAvailable() {
  if (selectedChecked.value.length === 0) return
  const drop = new Set(selectedChecked.value.map(String))
  data.value.selected_items_ids = (data.value.selected_items_ids ?? [])
    .map(String)
    .filter((id: string) => !drop.has(id))
  selectedChecked.value = []
}
function moveAllToAvailable() {
  data.value.selected_items_ids = []
  selectedChecked.value = []
}

/* ─────────────────  child-row helpers  ───────────────── */

function addHeroSlide() {
  data.value.hero_slides.push({
    image: '', eyebrow: '', headline: '', subheadline: '',
    primary_cta_label: '', primary_cta_link: '',
    secondary_cta_label: '', secondary_cta_link: '',
    bg_color: '', text_color: '', alignment: 'left',
    sort_order: data.value.hero_slides.length, active: true,
  })
}
function removeHeroSlide(i: number) { data.value.hero_slides.splice(i, 1) }
function moveHeroSlide(i: number, dir: -1 | 1) {
  const arr = data.value.hero_slides
  const j = i + dir
  if (j < 0 || j >= arr.length) return
  ;[arr[i], arr[j]] = [arr[j], arr[i]]
  arr.forEach((s: any, idx: number) => (s.sort_order = idx))
}

function addValueProp() {
  data.value.value_props.push({
    icon: '', title: '', body: '',
    sort_order: data.value.value_props.length, active: true,
  })
}
function removeValueProp(i: number) { data.value.value_props.splice(i, 1) }

// Promo cards — "Featured Promotions" strip on the storefront homepage.
// Same edit pattern as value_props: icon is full inline SVG markup
// rendered through a live preview swatch on the side. The storefront's
// SmartIcon component wraps it as `data:image/svg+xml,…` and renders
// via <img>. `tone` drives the card's gradient backdrop (primary=green,
// secondary=orange) so a tenant can mix tones for visual rhythm.
//
// `DEFAULT_PROMO_CARDS` mirrors the three cards the storefront falls
// back to when no promo_cards row exists — Ramadan Deals / Resort Bulk
// Pack / New Arrivals. Material Symbol glyph names ("mosque",
// "sailing", "auto_awesome") were translated to inline SVG so the
// admin can edit the icon directly without needing a font dependency.
// Clicking "Load defaults" pushes them all into the form for a tenant
// to customise.
const DEFAULT_PROMO_CARDS = [
  {
    title: 'Ramadan Deals',
    body: 'Bulk discounts for the holy month.',
    link: '/search?promo=ramadan',
    tone: 'primary',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"/><path d="m18.5 2 .5 1.5L20.5 4 19 4.5 18.5 6 18 4.5 16.5 4 18 3.5Z"/></svg>`,
  },
  {
    title: 'Resort Bulk Pack',
    body: 'Custom logistics for tourism partners.',
    link: '/search?customer_group=resort',
    tone: 'secondary',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18h18l-2 4H5l-2-4Z"/><path d="M12 2v16"/><path d="M12 4 5 18"/><path d="M12 4l7 14"/><path d="M3 21c1.5.7 3 .7 4.5 0S10.5 20.3 12 21s3 .7 4.5 0 3-.7 4.5 0"/></svg>`,
  },
  {
    title: 'New Arrivals',
    body: "Explore this week's fresh imports.",
    link: '/search?sort=newest',
    tone: 'primary',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.287 1.288L3 12l5.8 1.9a2 2 0 0 1 1.288 1.287L12 21l1.9-5.8a2 2 0 0 1 1.287-1.288L21 12l-5.8-1.9a2 2 0 0 1-1.288-1.287Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>`,
  },
]

function addPromoCard() {
  data.value.promo_cards.push({
    title: '', body: '', icon: '', link: '', tone: 'primary',
    sort_order: data.value.promo_cards.length, active: true,
  })
}
function removePromoCard(i: number) { data.value.promo_cards.splice(i, 1) }
function movePromoCard(i: number, dir: -1 | 1) {
  const arr = data.value.promo_cards
  const j = i + dir
  if (j < 0 || j >= arr.length) return
  ;[arr[i], arr[j]] = [arr[j], arr[i]]
  arr.forEach((c: any, idx: number) => (c.sort_order = idx))
}

// Append the three storefront-default cards. Safe to call when the
// list is non-empty too — sort_order is set off the current length so
// loaded defaults land at the bottom rather than overwriting existing
// admin-curated cards.
function loadDefaultPromoCards() {
  const base = data.value.promo_cards.length
  DEFAULT_PROMO_CARDS.forEach((d, idx) => {
    data.value.promo_cards.push({
      ...d,
      sort_order: base + idx,
      active: true,
    })
  })
}

/**
 * Live preview helper for the value-prop SVG icon textarea. Strips the
 * common XSS vectors (script blocks, on* handlers, javascript: URIs) before
 * the markup is passed to v-html, so a pasted icon can't introduce script
 * even while the admin is still typing. Returns '' when the input doesn't
 * look like SVG so the empty-state placeholder renders instead.
 */
function svgPreview(raw: unknown): string {
  const s = String(raw ?? '').trim()
  if (!s || !/^<svg[\s>]/i.test(s)) return ''
  return s
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript\s*:/gi, '')
}

function addNavItem() {
  data.value.nav_items.push({
    label: '', link: '',
    sort_order: data.value.nav_items.length, active: true,
  })
}
function removeNavItem(i: number) { data.value.nav_items.splice(i, 1) }

function addFooterColumn() {
  data.value.footer_columns.push({
    heading: '', links: [],
    sort_order: data.value.footer_columns.length, active: true,
  })
}
function removeFooterColumn(i: number) { data.value.footer_columns.splice(i, 1) }
function addFooterLink(colIdx: number) {
  data.value.footer_columns[colIdx].links.push({
    label: '', link: '',
    sort_order: data.value.footer_columns[colIdx].links.length, active: true,
  })
}
function removeFooterLink(colIdx: number, linkIdx: number) {
  data.value.footer_columns[colIdx].links.splice(linkIdx, 1)
}

function addPage() {
  data.value.pages.push({
    slug: '', title: '', body_html: '',
    show_in_footer: false, footer_column: '',
    sort_order: data.value.pages.length, active: true,
  })
}
function removePage(i: number) { data.value.pages.splice(i, 1) }

function addFaq() {
  data.value.faqs.push({
    question: '', answer: '',
    sort_order: data.value.faqs.length, active: true,
  })
}
function removeFaq(i: number) { data.value.faqs.splice(i, 1) }

const hasRecord = computed(() => !!data.value)
</script>

<template>
  <div class="min-h-full">
    <!-- ──  Sticky save bar  ────────────────────────────────────── -->
    <div class="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <div class="px-6 py-3 flex items-center gap-3">
        <h1 class="text-lg font-semibold text-gray-900 dark:text-white">Ecommerce setup</h1>
        <span class="text-xs text-gray-500 dark:text-gray-400 hidden md:inline">
          Storefront identity, layout, and dynamic content.
        </span>
        <div class="flex-1" />
        <button
          type="button"
          class="px-4 py-2 rounded-md text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
          :disabled="loading || saving || !hasRecord"
          @click="save"
        >
          <IconComponent v-if="!saving" name="Save" class="w-4 h-4" />
          <span v-else class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          {{ saving ? 'Saving…' : 'Save' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="p-12 text-center text-gray-500">Loading…</div>

    <div v-else-if="hasRecord" class="grid grid-cols-12 gap-6 p-6">
      <!-- ──  Side nav  ───────────────────────────────────────── -->
      <aside class="hidden lg:block col-span-2 sticky top-16 self-start">
        <nav class="space-y-1 text-sm">
          <button
            v-for="s in sections" :key="s.id"
            type="button"
            class="block w-full text-left px-3 py-1.5 rounded transition"
            :class="
              activeSection === s.id
                ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-semibold'
                : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
            "
            @click="pickSection(s.id)"
          >{{ s.label }}</button>
        </nav>
      </aside>

      <!-- ──  Sectioned form  ─────────────────────────────────── -->
      <div class="col-span-12 lg:col-span-10">
        <!-- Mobile section picker — replaces the hidden side nav on small screens. -->
        <div class="lg:hidden mb-4">
          <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Section</label>
          <select
            :value="activeSection"
            class="appearance-none w-full border border-gray-300 dark:border-gray-600 bg-transparent rounded-md px-3 py-2 text-sm"
            @change="onSectionChange"
          >
            <option v-for="s in sections" :key="s.id" :value="s.id">{{ s.label }}</option>
          </select>
        </div>

        <!-- Single-section heading -->
        <div class="mb-4 lg:hidden flex items-baseline gap-2 text-xs text-gray-500">
          <span>Showing</span>
          <span class="font-semibold text-gray-800 dark:text-gray-200">{{ activeLabel }}</span>
        </div>

        <div class="space-y-10">

        <!-- ───── Identity ─────
             Single consolidated pane per spec: operational pins (warehouse,
             sales channel, storefront-documents user), brand identity (site
             copy, logo / favicon / hero image / brand color — moved from
             the deleted Branding pane), commerce essentials (currency, price
             list, tax-inclusive, items selection w/ transfer-list, allowed
             payment types — moved from Commerce). -->
        <section v-show="activeSection === 'identity'" id="sec-identity">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Identity</h2>

          <!-- Operational pins.
               Per the latest spec, Outlet and Channel type are no longer
               surfaced — the row keeps the previously-saved values in DB,
               new rows default to `type='Website'`. The * marker flags
               compulsory fields that the save guard validates against. -->
          <div class="grid md:grid-cols-3 gap-4">
            <div :class="missing.warehouses_id ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''">
              <SearchSelect v-model="data.warehouses_id" label="Warehouse *" end-point="system/company/warehouses" />
            </div>
            <div :class="missing.sales_channels_id ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''">
              <SearchSelect v-model="data.sales_channels_id" label="Sales channel *" end-point="revenue/master-data/sales-channels" />
            </div>
            <!--
              Every sales order / cart the storefront creates is written with
              this user as `users_id`. Reporting and permission audits depend
              on it pointing at a real POS staff user — leave blank and the
              records arrive un-attributed.
            -->
            <div :class="missing.document_users_id ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''">
              <SearchSelect
                v-model="data.document_users_id"
                label="User for storefront documents *"
                end-point="system/security/users"
              />
            </div>
            <CheckInput v-model="data.public_storefront_enabled" label="Public storefront enabled" />
          </div>

          <!-- Site copy -->
          <div class="grid md:grid-cols-3 gap-4 pt-3">
            <div :class="missing.site_name ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''">
              <TextInput v-model="data.site_name" label="Site name *" placeholder="Vaaly Traders" />
            </div>
            <div :class="missing.tagline ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''">
              <TextInput v-model="data.tagline" label="Tag line *" placeholder="Wholesale, simplified." />
            </div>
            <div :class="missing.copyright_text ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''">
              <TextInput v-model="data.copyright_text" label="Copyright *" placeholder="© 2026 Your Store" />
            </div>
          </div>

          <!-- Site URL — on its own row because URLs are long and the
               controller mirrors this into the admin-side `allowed_origins`
               table on save so the storefront can pass CORS. Changing it
               here also retires the previous origin. -->
          <div class="pt-3">
            <div :class="missing.site_url ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''">
              <TextInput v-model="data.site_url" label="Site URL *" placeholder="https://shop.example.mv" />
            </div>
          </div>

          <!-- Brand assets (was the Branding pane) -->
          <div class="grid md:grid-cols-2 gap-4 pt-3">
            <FileUpload
              accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
              label="Logo"
              :max-size="5"
              :multiple="false"
              :readonly="isReadOnly"
              :image="true"
              path="ecommerce_setups"
              v-model="data.logo"
            />
            <FileUpload
              accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
              label="Favicon"
              :max-size="5"
              :multiple="false"
              :readonly="isReadOnly"
              :image="true"
              path="ecommerce_setups"
              v-model="data.favicon"
            />
          </div>

          <!-- Brand color + commerce essentials -->
          <div class="grid md:grid-cols-3 gap-4 pt-3 items-end">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand color</label>
              <input v-model="data.brand_color" type="color" class="h-9 w-16 cursor-pointer rounded-md border border-gray-300 dark:border-gray-600 bg-transparent" />
            </div>
            <div :class="missing.default_currency ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''">
              <SearchSelect v-model="data.default_currency" label="Currency *" :options="currencies" />
            </div>
            <div :class="missing.default_price_lists_id ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''">
              <SearchSelect v-model="data.default_price_lists_id" label="Price list *" end-point="finance/master-data/price-lists" />
            </div>
          </div>

          <!-- Catalog scoping toggles -->
          <div class="grid md:grid-cols-3 gap-4 pt-3">
            <CheckInput v-model="data.tax_inclusive_pricing" label="Tax-inclusive pricing" />
            <CheckInput v-model="data.only_selected_items" label="Only selected items" />
            <!-- Hard storefront filter: skip any item with on-hand ≤ 0 at the
                 configured warehouse. Stricter than "Show out-of-stock" in
                 Catalog display (which only hides the badge). -->
            <CheckInput v-model="data.only_in_stock_items" label="Display only items with available stock" />
          </div>

          <!-- Outlet scoping — optional. When set, the connector's
               products() endpoint only returns items whose `outlets`
               CSV contains this outlet's id. Leaving it blank reverts
               to the default behaviour (every item, no outlet pin). -->
          <div class="grid md:grid-cols-2 gap-4 pt-3">
            <SearchSelect
              v-model="data.outlets_id"
              label="Outlet (optional)"
              end-point="system/company/outlets"
            />
          </div>

          <!-- Item-group scoping — mirrors the "only_selected_items"
               toggle but at the item_groups level. When ON the
               SearchSelectMultiItems below appears; storefront
               categories() then only returns the picked groups, and
               products() intersects the items.item_groups CSV with the
               same list. Empty picker = no filter applied (so an admin
               can flip the toggle on first and pick groups after). -->
          <div class="grid md:grid-cols-3 gap-4 pt-3">
            <CheckInput v-model="data.only_selected_item_groups" label="Only selected item groups" />
          </div>
          <div
            v-if="data.only_selected_item_groups"
            class="grid md:grid-cols-1 gap-4 pt-3"
          >
            <SearchSelectMultiItems
              v-model="data.selected_item_groups_ids"
              label="Item groups"
              :options="allItemGroups"
              :remote="true"
            />
          </div>

          <!-- Allowed payment types — at least one must be picked per spec. -->
          <div
            class="grid md:grid-cols-1 gap-4 pt-3"
            :class="missing.allowed_payment_types_ids ? 'ring-1 ring-red-400 rounded-md p-0.5' : ''"
          >
            <SearchSelectMultiItems
              v-model="data.allowed_payment_types_ids"
              label="Allowed payment types *"
              end-point="finance/master-data/payment-types"
            />
          </div>

          <!-- Transfer list (visible only when `only_selected_items` is on) -->
          <div
            v-if="data.only_selected_items"
            class="pt-4 mt-3 border-t border-dashed border-gray-200 dark:border-gray-700"
          >
            <div class="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2 uppercase tracking-wide">
              Storefront items
            </div>
            <div class="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3">
              <!-- Available -->
              <div class="border border-gray-200 dark:border-gray-700 rounded-md flex flex-col">
                <div class="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <div class="text-xs font-semibold text-gray-700 dark:text-gray-200">Available</div>
                  <input
                    v-model="availableFilter"
                    type="search"
                    placeholder="Search available…"
                    class="mt-1 w-full text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
                    :disabled="itemsLoading"
                  />
                </div>
                <div class="overflow-auto max-h-64 text-sm">
                  <div v-if="itemsLoading" class="p-3 text-xs text-gray-500">Loading items…</div>
                  <label
                    v-for="it in availableItems"
                    :key="`avail-${it.id}`"
                    class="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-800"
                  >
                    <input v-model="availableChecked" type="checkbox" :value="String(it.id)" />
                    <span class="truncate">
                      <span v-if="it.code" class="text-xs text-gray-500 mr-1">{{ it.code }}</span>
                      {{ it.name }}
                    </span>
                  </label>
                  <div v-if="!itemsLoading && availableItems.length === 0" class="p-3 text-xs text-gray-500">
                    Nothing to move.
                  </div>
                </div>
              </div>

              <!-- Move buttons -->
              <div class="flex md:flex-col items-center justify-center gap-2 md:py-4">
                <button type="button" class="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-gray-800 disabled:opacity-40" :disabled="availableChecked.length === 0" title="Move selected" @click="moveToSelected">→</button>
                <button type="button" class="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-gray-800 disabled:opacity-40" :disabled="availableItems.length === 0" title="Move all" @click="moveAllToSelected">»</button>
                <button type="button" class="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-gray-800 disabled:opacity-40" :disabled="selectedChecked.length === 0" title="Remove selected" @click="moveToAvailable">←</button>
                <button type="button" class="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-gray-800 disabled:opacity-40" :disabled="selectedItems.length === 0" title="Remove all" @click="moveAllToAvailable">«</button>
              </div>

              <!-- Selected -->
              <div class="border border-blue-300 dark:border-blue-700 rounded-md flex flex-col">
                <div class="px-3 py-2 border-b border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30">
                  <div class="text-xs font-semibold text-blue-700 dark:text-blue-200">
                    Selected <span class="font-normal">({{ selectedItems.length }})</span>
                  </div>
                  <input
                    v-model="selectedFilter"
                    type="search"
                    placeholder="Search selected…"
                    class="mt-1 w-full text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
                  />
                </div>
                <div class="overflow-auto max-h-64 text-sm">
                  <label
                    v-for="it in selectedItems"
                    :key="`sel-${it.id}`"
                    class="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-800"
                  >
                    <input v-model="selectedChecked" type="checkbox" :value="String(it.id)" />
                    <span class="truncate">
                      <span v-if="it.code" class="text-xs text-gray-500 mr-1">{{ it.code }}</span>
                      {{ it.name }}
                    </span>
                  </label>
                  <div v-if="selectedItems.length === 0" class="p-3 text-xs text-gray-500">
                    No items pinned yet — pick from the Available column.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
            The selected user is recorded as the creator on every sales order
            submitted from the storefront.
          </p>
        </section>

        <!-- Announcement bar -->
        <section v-show="activeSection === 'announcement'" id="sec-announcement">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Announcement bar</h2>
          <div class="grid md:grid-cols-3 gap-4">
            <CheckInput v-model="data.announcement_enabled" label="Show announcement strip" />
            <TextInput v-model="data.announcement_text" label="Announcement text" placeholder="Free delivery on orders above MVR 5,000" class="md:col-span-2" />
          </div>
        </section>

        <!-- Hero slides (carousel) -->
        <section v-show="activeSection === 'hero'" id="sec-hero">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Hero slides</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addHeroSlide">+ Add slide</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">
            Each slide has its own image, copy, and CTA buttons. Slides render in sort order as a carousel
            on the home page. One slide = a single static hero.
          </p>

          <div v-if="data.hero_slides.length === 0" class="text-sm text-gray-400 italic">
            No hero slides yet. Click <em>Add slide</em> to create the first.
          </div>

          <div
            v-for="(s, i) in data.hero_slides"
            :key="i"
            class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4"
          >
            <!-- Slide header bar: order, up/down/remove -->
            <div class="flex items-center justify-between mb-3">
              <div class="text-xs uppercase tracking-wide font-semibold text-gray-500">
                Slide {{ i + 1 }}
              </div>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
                  :disabled="i === 0"
                  aria-label="Move slide up"
                  @click="moveHeroSlide(i, -1)"
                >
                  <IconComponent name="ChevronUp" class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
                  :disabled="i === data.hero_slides.length - 1"
                  aria-label="Move slide down"
                  @click="moveHeroSlide(i, 1)"
                >
                  <IconComponent name="ChevronDown" class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 rounded text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                  aria-label="Remove slide"
                  @click="removeHeroSlide(i)"
                >
                  <IconComponent name="Trash2" class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Slide media + copy -->
            <div class="grid md:grid-cols-3 gap-4">
              <FileUpload
                accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
                label="Slide image"
                :max-size="5"
                :multiple="false"
                :readonly="isReadOnly"
                :image="true"
                path="ecommerce_setups"
                v-model="s.image"
              />
              <TextInput v-model="s.eyebrow" label="Eyebrow" placeholder="ESTABLISHED 1982" />
              <SearchSelect
                v-model="s.alignment"
                label="Text alignment"
                :options="[{ id: 'left', name: 'Left' }, { id: 'center', name: 'Center' }, { id: 'right', name: 'Right' }]"
              />
              <TextInput v-model="s.headline" label="Headline" placeholder="Wholesale supplies, delivered island-wide" class="md:col-span-2" />
              <CheckInput v-model="s.active" label="Active" />
              <TextAreaInput v-model="s.subheadline" label="Sub-headline" :rows="2" class="md:col-span-3" />
            </div>

            <!-- CTAs -->
            <div class="mt-4 grid md:grid-cols-2 gap-4">
              <div class="border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div class="text-xs uppercase tracking-wide text-gray-500 mb-2">Primary button</div>
                <div class="grid grid-cols-2 gap-3">
                  <TextInput v-model="s.primary_cta_label" label="Label" placeholder="Shop now" />
                  <TextInput v-model="s.primary_cta_link" label="Link" placeholder="/search" />
                </div>
              </div>
              <div class="border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div class="text-xs uppercase tracking-wide text-gray-500 mb-2">Secondary button</div>
                <div class="grid grid-cols-2 gap-3">
                  <TextInput v-model="s.secondary_cta_label" label="Label" placeholder="Bulk pricing" />
                  <TextInput v-model="s.secondary_cta_link" label="Link" placeholder="/about" />
                </div>
              </div>
            </div>

            <!-- Colours -->
            <div class="mt-4 grid md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Background colour</label>
                <div class="flex items-center gap-3">
                  <input v-model="s.bg_color" type="color" class="h-9 w-16 cursor-pointer rounded-md border border-gray-300 dark:border-gray-600 bg-transparent" />
                  <input v-model="s.bg_color" type="text" placeholder="#0d6efd" class="appearance-none flex-1 border border-gray-300 dark:border-gray-600 bg-transparent rounded-md px-3 py-2 text-sm font-mono" />
                </div>
                <p class="text-xs text-gray-500 mt-1">Used behind the image — fallback when no slide image is set.</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Text colour</label>
                <div class="flex items-center gap-3">
                  <input v-model="s.text_color" type="color" class="h-9 w-16 cursor-pointer rounded-md border border-gray-300 dark:border-gray-600 bg-transparent" />
                  <input v-model="s.text_color" type="text" placeholder="#ffffff" class="appearance-none flex-1 border border-gray-300 dark:border-gray-600 bg-transparent rounded-md px-3 py-2 text-sm font-mono" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Value props -->
        <section v-show="activeSection === 'value_props'" id="sec-value_props">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Value-prop cards</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addValueProp">+ Add</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">
            Cards under the hero (Island-wide delivery, Net-30, etc.). Paste an
            <code>&lt;svg&gt;</code> for the icon — the preview swatch on the
            left updates live. Grab clean stroke icons from
            <a href="https://lucide.dev" target="_blank" rel="noopener" class="text-blue-600 hover:underline">lucide.dev</a> or
            <a href="https://heroicons.com" target="_blank" rel="noopener" class="text-blue-600 hover:underline">heroicons.com</a>.
          </p>
          <div v-if="data.value_props.length === 0" class="text-sm text-gray-400 italic">
            No value-prop cards yet. Click <em>Add</em> to create the first.
          </div>

          <div
            v-for="(v, i) in data.value_props"
            :key="i"
            class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4"
          >
            <div class="grid md:grid-cols-12 gap-4 items-start">
              <!-- Live preview swatch -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preview</label>
                <div
                  class="w-full aspect-square rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-gray-400 overflow-hidden p-3"
                >
                  <div
                    v-if="svgPreview(v.icon)"
                    v-html="svgPreview(v.icon)"
                    class="w-full h-full [&_svg]:w-full [&_svg]:h-full text-blue-600"
                    aria-hidden="true"
                  />
                  <span v-else class="text-xs text-center leading-tight">Paste<br/>SVG</span>
                </div>
              </div>

              <!-- Title / Body / Order / Remove -->
              <div class="md:col-span-10 grid md:grid-cols-12 gap-3">
                <TextInput v-model="v.title" label="Title" class="md:col-span-5" />
                <TextInput v-model="v.body" label="Body" class="md:col-span-5" />
                <NumberInput v-model="v.sort_order" label="Order" :show-controls="false" class="md:col-span-1" />
                <div class="md:col-span-1 pt-7 flex justify-end">
                  <button type="button" class="text-red-500 hover:text-red-700" @click="removeValueProp(i)">
                    <IconComponent name="Trash2" class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <!-- SVG textarea, full width -->
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon (SVG markup)</label>
              <textarea
                v-model="v.icon"
                :disabled="isReadOnly"
                rows="4"
                placeholder='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">…</svg>'
                spellcheck="false"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-transparent rounded-md text-xs font-mono focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p class="text-xs text-gray-500 mt-1">
                Use <code>stroke="currentColor"</code> or <code>fill="currentColor"</code> so the icon picks up
                the card's text colour automatically.
              </p>
            </div>
          </div>
        </section>

        <!-- Promo cards — homepage "Featured Promotions" strip -->
        <section v-show="activeSection === 'promo_cards'" id="sec-promo_cards">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Promo cards</h2>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="text-sm text-blue-600 hover:underline"
                @click="loadDefaultPromoCards"
              >Load defaults (Ramadan / Resort / New Arrivals)</button>
              <button
                type="button"
                class="text-sm text-blue-600 hover:underline"
                @click="addPromoCard"
              >+ Add</button>
            </div>
          </div>
          <p class="text-xs text-gray-500 mb-4">
            Tiles in the homepage's "Featured Promotions" strip. Each card has
            its own title, blurb, SVG icon, CTA link, and gradient tone.
            Storefront renders them in <em>Order</em>; an empty list falls back
            to the built-in default cards. Paste an
            <code>&lt;svg&gt;</code> for the icon — the preview swatch on the
            left updates live. Grab clean stroke icons from
            <a href="https://lucide.dev" target="_blank" rel="noopener" class="text-blue-600 hover:underline">lucide.dev</a> or
            <a href="https://heroicons.com" target="_blank" rel="noopener" class="text-blue-600 hover:underline">heroicons.com</a>.
          </p>
          <div v-if="data.promo_cards.length === 0" class="text-sm text-gray-400 italic">
            No promo cards yet. Click <em>Load defaults</em> to seed the three
            built-in storefront cards, or <em>Add</em> to start from scratch.
          </div>

          <div
            v-for="(p, i) in data.promo_cards"
            :key="i"
            class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4"
          >
            <!-- Card header bar: order, up/down/remove -->
            <div class="flex items-center justify-between mb-3">
              <div class="text-xs uppercase tracking-wide font-semibold text-gray-500">
                Card {{ i + 1 }}
              </div>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
                  :disabled="i === 0"
                  aria-label="Move up"
                  @click="movePromoCard(i, -1)"
                >
                  <IconComponent name="ChevronUp" class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
                  :disabled="i === data.promo_cards.length - 1"
                  aria-label="Move down"
                  @click="movePromoCard(i, 1)"
                >
                  <IconComponent name="ChevronDown" class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 rounded text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                  aria-label="Remove"
                  @click="removePromoCard(i)"
                >
                  <IconComponent name="Trash2" class="w-4 h-4" />
                </button>
              </div>
            </div>

            <div class="grid md:grid-cols-12 gap-4 items-start">
              <!-- Live SVG preview swatch — mirrors value_props.
                   `svgPreview` is the shared XSS-stripping helper that
                   sanitises script blocks, on* handlers and
                   `javascript:` URIs before v-html mounts. The tone
                   selection drives the swatch's text colour so the
                   admin can see how the icon will look against the
                   gradient backdrop. -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preview</label>
                <div
                  class="w-full aspect-square rounded-md border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden p-3 text-white"
                  :class="p.tone === 'secondary'
                    ? 'bg-gradient-to-br from-orange-400 to-orange-600'
                    : 'bg-gradient-to-br from-emerald-500 to-emerald-700'"
                >
                  <div
                    v-if="svgPreview(p.icon)"
                    v-html="svgPreview(p.icon)"
                    class="w-full h-full [&_svg]:w-full [&_svg]:h-full"
                    aria-hidden="true"
                  />
                  <span v-else class="text-xs text-center leading-tight opacity-80">Paste<br/>SVG</span>
                </div>
              </div>

              <div class="md:col-span-10 grid md:grid-cols-12 gap-3">
                <TextInput v-model="p.title" label="Title" class="md:col-span-5" />
                <TextInput v-model="p.body" label="Body" class="md:col-span-7" />
                <TextInput
                  v-model="p.link"
                  label="CTA link"
                  placeholder="/search?promo=ramadan  /  https://shop.example.mv/promo"
                  class="md:col-span-7"
                />
                <SearchSelect
                  v-model="p.tone"
                  label="Tone"
                  :options="[{ id: 'primary', name: 'Primary (green)' }, { id: 'secondary', name: 'Secondary (orange)' }]"
                  class="md:col-span-4"
                />
                <NumberInput v-model="p.sort_order" label="Order" :show-controls="false" class="md:col-span-1" />
              </div>
            </div>

            <!-- SVG textarea, full width — same shape as the value_props
                 icon editor. `stroke="currentColor"` makes the icon
                 inherit the tile's white text colour automatically. -->
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon (SVG markup)</label>
              <textarea
                v-model="p.icon"
                :disabled="isReadOnly"
                rows="4"
                placeholder='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">…</svg>'
                spellcheck="false"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-transparent rounded-md text-xs font-mono focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p class="text-xs text-gray-500 mt-1">
                Use <code>stroke="currentColor"</code> or <code>fill="currentColor"</code> so the icon picks up
                the card's white text colour automatically. The storefront's
                SmartIcon wraps inline SVG as <code>data:image/svg+xml</code>
                and renders it through <code>&lt;img&gt;</code>.
              </p>
            </div>
          </div>
        </section>

        <!-- Top nav -->
        <section v-show="activeSection === 'nav'" id="sec-nav">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Top navigation</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addNavItem">+ Add</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">Items shown alongside Home in the header.</p>
          <div v-if="data.nav_items.length === 0" class="text-sm text-gray-400 italic">Only the home link will render in the header.</div>
          <div v-for="(n, i) in data.nav_items" :key="i" class="grid md:grid-cols-12 gap-3 items-start mb-3">
            <TextInput v-model="n.label" label="Label" class="md:col-span-4" />
            <TextInput v-model="n.link" label="Link" placeholder="/about" class="md:col-span-5" />
            <NumberInput v-model="n.sort_order" label="Order" :show-controls="false" class="md:col-span-2" />
            <div class="md:col-span-1 pt-7">
              <button type="button" class="text-red-500 hover:text-red-700" @click="removeNavItem(i)">
                <IconComponent name="Trash2" class="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>

        <!-- Footer columns + links -->
        <section v-show="activeSection === 'footer'" id="sec-footer">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Footer columns</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addFooterColumn">+ Add column</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">Each column has a heading and a list of links. Order columns left → right with sort order.</p>
          <div v-if="data.footer_columns.length === 0" class="text-sm text-gray-400 italic">No footer columns. The footer will render brand + tagline only.</div>
          <div v-for="(c, ci) in data.footer_columns" :key="ci" class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4">
            <div class="grid md:grid-cols-12 gap-3 items-start">
              <TextInput v-model="c.heading" label="Heading" placeholder="Shop / Account / Company" class="md:col-span-5" />
              <NumberInput v-model="c.sort_order" label="Order" :show-controls="false" class="md:col-span-2" />
              <div class="md:col-span-5 pt-7 flex justify-end">
                <button type="button" class="text-red-500 hover:text-red-700 inline-flex items-center gap-1" @click="removeFooterColumn(ci)">
                  <IconComponent name="Trash2" class="w-4 h-4" />
                  <span class="text-xs">Remove column</span>
                </button>
              </div>
            </div>
            <div class="mt-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
              <div class="flex items-baseline justify-between mb-2">
                <span class="text-xs uppercase tracking-wide text-gray-500">Links</span>
                <button type="button" class="text-xs text-blue-600 hover:underline" @click="addFooterLink(ci)">+ Add link</button>
              </div>
              <div v-for="(l, li) in c.links" :key="li" class="grid md:grid-cols-12 gap-2 items-start mb-2">
                <TextInput v-model="l.label" label="Label" class="md:col-span-4" />
                <TextInput v-model="l.link" label="URL" class="md:col-span-5" />
                <NumberInput v-model="l.sort_order" label="Order" :show-controls="false" class="md:col-span-2" />
                <div class="md:col-span-1 pt-7">
                  <button type="button" class="text-red-500 hover:text-red-700" @click="removeFooterLink(ci, li)">
                    <IconComponent name="Trash2" class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Pages -->
        <section v-show="activeSection === 'pages'" id="sec-pages">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Content pages</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addPage">+ Add</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">About, Contact, FAQ, and any custom page. Slug becomes the URL: <code>/p/&lt;slug&gt;</code>.</p>
          <div v-if="data.pages.length === 0" class="text-sm text-gray-400 italic">No custom pages yet.</div>
          <div v-for="(p, i) in data.pages" :key="i" class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4">
            <div class="grid md:grid-cols-12 gap-3 items-start">
              <TextInput v-model="p.slug" label="Slug" placeholder="about" class="md:col-span-3" />
              <TextInput v-model="p.title" label="Title" placeholder="About us" class="md:col-span-4" />
              <TextInput v-model="p.footer_column" label="Footer column heading" class="md:col-span-3" />
              <CheckInput v-model="p.show_in_footer" label="Show in footer" class="md:col-span-1 pt-7" />
              <div class="md:col-span-1 pt-7 flex justify-end">
                <button type="button" class="text-red-500 hover:text-red-700" @click="removePage(i)">
                  <IconComponent name="Trash2" class="w-4 h-4" />
                </button>
              </div>
            </div>
            <TextAreaInput v-model="p.body_html" label="Body (HTML)" :rows="5" class="mt-3" />
          </div>
        </section>

        <!-- FAQs -->
        <section v-show="activeSection === 'faqs'" id="sec-faqs">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">FAQs</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addFaq">+ Add</button>
          </div>
          <div v-if="data.faqs.length === 0" class="text-sm text-gray-400 italic">No FAQs.</div>
          <div v-for="(f, i) in data.faqs" :key="i" class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4">
            <div class="grid md:grid-cols-12 gap-3 items-start">
              <TextInput v-model="f.question" label="Question" class="md:col-span-9" />
              <NumberInput v-model="f.sort_order" label="Order" :show-controls="false" class="md:col-span-2" />
              <div class="md:col-span-1 pt-7 flex justify-end">
                <button type="button" class="text-red-500 hover:text-red-700" @click="removeFaq(i)">
                  <IconComponent name="Trash2" class="w-4 h-4" />
                </button>
              </div>
            </div>
            <TextAreaInput v-model="f.answer" label="Answer" :rows="3" class="mt-3" />
          </div>
        </section>

        <!-- Contact & social -->
        <section v-show="activeSection === 'contact'" id="sec-contact">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Contact & social</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <TextInput v-model="data.support_email" label="Support email" type="email" />
            <TextInput v-model="data.support_phone" label="Support phone" />
            <TextInput v-model="data.whatsapp_number" label="WhatsApp number" />
            <TextInput v-model="data.physical_address" label="Physical address" />
            <TextInput v-model="data.facebook_url" label="Facebook URL" />
            <TextInput v-model="data.instagram_url" label="Instagram URL" />
            <TextInput v-model="data.tiktok_url" label="TikTok URL" />
          </div>
        </section>

        <!-- Commerce behaviour pane removed per spec — the surviving
             knobs (guest checkout / show prices / minimum order / default
             customer group) were dropped; everything else already lives
             on Identity. -->

        <!-- Catalog display -->
        <section v-show="activeSection === 'catalog'" id="sec-catalog">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Catalog display</h2>
          <div class="grid md:grid-cols-4 gap-4">
            <NumberInput v-model="data.products_per_page" label="Products per page" :min="1" />
            <NumberInput v-model="data.no_of_top_offer_products" label="Top offers count" :min="0" />
            <NumberInput v-model="data.no_of_best_selling_products" label="Best sellers count" :min="0" />
            <NumberInput v-model="data.no_of_new_arrival_products" label="New arrivals count" :min="0" />
            <CheckInput v-model="data.show_out_of_stock_products" label="Show out-of-stock" />
            <CheckInput v-model="data.allow_pre_orders" label="Allow pre-orders" />
            <CheckInput v-model="data.show_points" label="Show loyalty points" />
            <CheckInput v-model="data.show_reviews" label="Show reviews" />
          </div>
        </section>

        <!-- SEO -->
        <section v-show="activeSection === 'seo'" id="sec-seo">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">SEO</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <TextInput v-model="data.meta_title" label="Default meta title" />
            <TextInput v-model="data.meta_description" label="Default meta description" />
            <TextInput v-model="data.google_analytics_id" label="Google Analytics ID" placeholder="G-XXXXXXXXXX" />
            <TextInput v-model="data.meta_pixel_id" label="Meta Pixel ID" />
          </div>
        </section>

        <!-- ───── Order notifications ─────
             Consolidates the old Notifications and Quotation-operations
             panes into one. Three multi-select pickers drive the fan-out:
             explicit users, outlets (the connector unions every staff user
             attached to each picked outlet), and individual POS stations
             (whose bound users are added on top). Optional admin-side email
             + SMS toggles stay so the old transactional template stack still
             works for tenants that haven't switched fully to staff-pings. -->
        <section v-show="activeSection === 'notifications'" id="sec-notifications">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Order notifications</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Who gets pinged when a storefront order is placed. Every picked
            user receives the notification, plus all staff users bound to the
            selected outlets and POS stations.
          </p>
          <div class="grid md:grid-cols-2 gap-4">
            <SearchSelectMultiItems
              v-model="data.notification_user_ids"
              label="Notifying users"
              end-point="system/security/users"
            />
            <SearchSelectMultiItems
              v-model="data.notification_outlets_ids"
              label="Notify to outlets"
              end-point="system/company/outlets"
            />
            <!-- Driven by the `filteredStations` computed: empty until at
                 least one outlet is picked, then narrows to that outlet's
                 stations. `:remote="true"` tells the multi-pick to use
                 `:options` instead of hitting its own list endpoint. -->
            <div>
              <SearchSelectMultiItems
                v-model="data.notification_outlet_pos_stations_ids"
                label="Notify to POS stations"
                :options="filteredStations"
                :remote="true"
              />
              <p
                v-if="(data.notification_outlets_ids ?? []).length === 0"
                class="mt-1 text-xs text-gray-500 dark:text-gray-400"
              >
                Pick an outlet first — stations load from the chosen outlets.
              </p>
            </div>
          </div>
          <div class="grid md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
            <TextInput v-model="data.order_admin_email" label="Admin notification email" type="email" />
            <TextAreaInput v-model="data.order_sms_template" label="Order SMS template" :rows="3" />
            <CheckInput v-model="data.send_order_email" label="Email admin on new order" />
            <CheckInput v-model="data.send_order_sms" label="SMS admin on new order" />
          </div>
        </section>

        <!-- Policies — four richtext editors so the admin can write
             formatted prose (headings, lists, links) without having to
             hand-author HTML. Output is still HTML on disk. -->
        <section v-show="activeSection === 'policies'" id="sec-policies">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Policies</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <RichTextEditor v-model="data.terms_html" label="Terms & conditions" />
            <RichTextEditor v-model="data.privacy_html" label="Privacy policy" />
            <RichTextEditor v-model="data.return_policy_html" label="Return policy" />
            <RichTextEditor v-model="data.shipping_policy_html" label="Shipping policy" />
          </div>
        </section>

        <!-- Featured content — three multi-pick dropdowns driven by the
             pre-loaded option arrays. The picker still persists CSVs on
             disk (the save flow joins the array back), so the data shape
             is unchanged. -->
        <section v-show="activeSection === 'featured'" id="sec-featured">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Featured content</h2>
          <div class="grid md:grid-cols-3 gap-4">
            <SearchSelectMultiItems
              v-model="data.featured_categories_ids"
              label="Featured categories"
              :options="allFeaturedCategories"
              :remote="true"
            />
            <SearchSelectMultiItems
              v-model="data.featured_brands_ids"
              label="Featured brands"
              :options="allFeaturedBrands"
              :remote="true"
            />
            <SearchSelectMultiItems
              v-model="data.featured_items_ids"
              label="Featured products"
              :options="allFeaturedProducts"
              :remote="true"
            />
          </div>
        </section>

        <!-- Operational -->
        <section v-show="activeSection === 'operational'" id="sec-operational">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Operational</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <CheckInput v-model="data.maintenance_mode" label="Maintenance mode" />
            <TextInput v-model="data.maintenance_message" label="Maintenance message" />
            <CheckInput v-model="data.active" label="Active" />
          </div>
        </section>

        </div>
      </div>
    </div>
  </div>
</template>
